import React from "react";
import { MTableToolbar } from "material-table";
import { createTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  LinearProgress,
  Select,
  Switch,
  Typography,
  Tooltip,
  Badge,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TableStyle from "./Style";

export const C2_FONT = "'Outfit', 'Poppins', sans-serif";
export const C2 = {
  sidebar: "#1A0F3B",
  dark: "#221448",
  deep: "#2C195B",
  mid: "#3B2275",
  violet: "#6411ED",
  bright: "#7628FF",
  bg: "#F4F2FE",
  white: "#FBFAFE",
  lavender: "#EBE7FB",
  border: "#D7CFEB",
  cellBorder: "#EDE8F7",
  muted: "#766293",
};

const fontOverride = { styleOverrides: { root: { fontFamily: C2_FONT } } };

export const c2TableTheme = createTheme({
  typography: { fontFamily: C2_FONT },
  palette: { primary: { main: C2.violet } },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: C2_FONT,
          padding: "6px 8px !important",
          fontSize: "0.82rem",
        },
        head: {
          padding: "8px 8px !important",
          fontSize: "0.78rem",
          fontWeight: 800,
        },
        body: {
          padding: "6px 8px !important",
        },
      },
    },
    MuiTypography: fontOverride,
    MuiButton: fontOverride,
    MuiInputBase: fontOverride,
    MuiInputLabel: fontOverride,
    MuiMenuItem: fontOverride,
    MuiTooltip: { styleOverrides: { tooltip: { fontFamily: C2_FONT } } },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: C2.violet,
          padding: "4px",
          "&:hover": { backgroundColor: "rgba(100,17,237,0.08)" },
          "&.Mui-disabled": { color: C2.border },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontFamily: C2_FONT, color: C2.muted },
        selectLabel: { fontFamily: C2_FONT },
        displayedRows: { fontFamily: C2_FONT },
      },
    },
  },
});

export const c2TableOptions = (selectedRow) => ({
  ...TableStyle(),
  pageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  padding: "dense",
  showTitle: false,
  searchFieldAlignment: "right",
  toolbarButtonAlignment: "right",
  headerStyle: {
    ...TableStyle().headerStyle,
    backgroundColor: C2.lavender,
    color: C2.sidebar,
    border: `1px solid ${C2.border}`,
    fontFamily: C2_FONT,
    fontWeight: 700,
    fontSize: "0.78em",
    whiteSpace: "normal",
    lineHeight: 1.25,
    padding: "8px 6px",
  },
  cellStyle: {
    ...TableStyle().cellStyle,
    border: `1px solid ${C2.cellBorder}`,
    fontFamily: C2_FONT,
    fontSize: "0.84em",
    color: C2.dark,
    padding: "6px 6px",
  },
  searchFieldStyle: {
    borderRadius: 10,
    border: `1px solid ${C2.border}`,
    padding: "2px 8px",
    fontFamily: C2_FONT,
    marginRight: 8,
  },
  rowStyle: (rowData) => ({
    backgroundColor:
      selectedRow === rowData.tableData.id ? C2.bg : "#FFFFFF",
  }),
});

export const c2TableStyle = (isRTL) => ({
  direction: isRTL === "rtl" ? "rtl" : "ltr",
  borderRadius: "12px",
  boxShadow: "none",
  padding: "4px",
  backgroundColor: "transparent",
});

export function C2TableCard({ children, sx }) {
  const cardRef = React.useRef(null);
  const scrollTargetRef = React.useRef(null);
  const trackRef = React.useRef(null);
  const isDraggingRef = React.useRef(false);

  const [state, setState] = React.useState({
    show: false,
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
    maxScroll: 0,
    rect: { left: 0, width: 0 },
  });

  React.useEffect(() => {
    const cardEl = cardRef.current;
    if (!cardEl) return;

    const findTarget = () => {
      const table = cardEl.querySelector("table");
      if (table) {
        let curr = table.parentElement;
        while (curr && curr !== cardEl) {
          const style = window.getComputedStyle(curr);
          const hasScrollStyle =
            style.overflowX === "auto" ||
            style.overflowX === "scroll" ||
            style.overflow === "auto" ||
            style.overflow === "scroll";
          if (hasScrollStyle && curr.scrollWidth > curr.clientWidth) {
            return curr;
          }
          curr = curr.parentElement;
        }
        curr = table.parentElement;
        while (curr && curr !== cardEl) {
          if (curr.scrollWidth > curr.clientWidth + 5) {
            return curr;
          }
          curr = curr.parentElement;
        }
        if (table.parentElement) return table.parentElement;
      }

      const divs = cardEl.querySelectorAll("div");
      for (let i = 0; i < divs.length; i++) {
        const d = divs[i];
        if (d.getAttribute("data-c2-scroll") === "true") continue;
        if (d.scrollWidth > d.clientWidth + 5) {
          const style = window.getComputedStyle(d);
          if (
            style.overflowX === "auto" ||
            style.overflowX === "scroll" ||
            style.overflow === "auto" ||
            style.overflow === "scroll"
          ) {
            return d;
          }
        }
      }
      return null;
    };

    let target = findTarget();
    scrollTargetRef.current = target;

    const updateMetrics = () => {
      const freshTarget = findTarget();
      if (freshTarget && freshTarget !== target) {
        if (target) {
          target.removeEventListener("scroll", onTargetScroll);
        }
        target = freshTarget;
        scrollTargetRef.current = target;
        target.addEventListener("scroll", onTargetScroll, { passive: true });
      }

      if (!target) {
        setState((prev) => (prev.show ? { ...prev, show: false } : prev));
        return;
      }

      const cardRect = cardEl.getBoundingClientRect();
      const scrollWidth = target.scrollWidth;
      const clientWidth = target.clientWidth;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const windowH = window.innerHeight || document.documentElement.clientHeight;

      // Visible si la tabla está en el viewport y tiene scroll horizontal
      const isCardVisible = cardRect.top < windowH - 40 && cardRect.bottom > 80;
      const hasOverflow = maxScroll > 20;

      if (hasOverflow && isCardVisible) {
        setState({
          show: true,
          scrollLeft: target.scrollLeft,
          scrollWidth,
          clientWidth,
          maxScroll,
          rect: {
            left: Math.max(16, cardRect.left),
            width: Math.min(cardRect.width, (window.innerWidth || 1200) - Math.max(16, cardRect.left) - 16),
          },
        });
      } else {
        setState((prev) => (prev.show ? { ...prev, show: false } : prev));
      }
    };

    const onTargetScroll = () => {
      if (target) {
        const maxScroll = Math.max(0, target.scrollWidth - target.clientWidth);
        setState((prev) => ({
          ...prev,
          scrollLeft: target.scrollLeft,
          maxScroll,
        }));
      }
    };

    if (target) {
      target.addEventListener("scroll", onTargetScroll, { passive: true });
    }

    window.addEventListener("scroll", updateMetrics, { passive: true });
    window.addEventListener("resize", updateMetrics, { passive: true });

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateMetrics());
      if (target) ro.observe(target);
      ro.observe(cardEl);
    }

    const intervalId = setInterval(updateMetrics, 500);

    return () => {
      clearInterval(intervalId);
      if (target) {
        target.removeEventListener("scroll", onTargetScroll);
      }
      window.removeEventListener("scroll", updateMetrics);
      window.removeEventListener("resize", updateMetrics);
      if (ro) ro.disconnect();
    };
  }, []);

  const handleScrollBy = (delta) => {
    const target = scrollTargetRef.current || (cardRef.current && cardRef.current.querySelector("table")?.parentElement);
    if (!target) return;
    if (typeof target.scrollBy === "function") {
      target.scrollBy({ left: delta, behavior: "smooth" });
    } else {
      target.scrollLeft += delta;
    }
    setTimeout(() => {
      const maxScroll = Math.max(0, target.scrollWidth - target.clientWidth);
      setState((prev) => ({
        ...prev,
        scrollLeft: target.scrollLeft,
        maxScroll,
      }));
    }, 100);
  };

  const applyProgressFromClientX = (clientX) => {
    const track = trackRef.current;
    const target = scrollTargetRef.current || (cardRef.current && cardRef.current.querySelector("table")?.parentElement);
    if (!track || !target) return;
    const rect = track.getBoundingClientRect();
    const pos = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const pct = rect.width > 0 ? pos / rect.width : 0;
    const maxScroll = Math.max(0, target.scrollWidth - target.clientWidth);
    target.scrollLeft = pct * maxScroll;
    setState((prev) => ({
      ...prev,
      scrollLeft: target.scrollLeft,
      maxScroll,
    }));
  };

  const handleTrackPointerDown = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    applyProgressFromClientX(e.clientX);

    const onPointerMove = (ev) => {
      if (!isDraggingRef.current) return;
      applyProgressFromClientX(ev.clientX);
    };

    const onPointerUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const progress =
    state.maxScroll > 0 ? Math.min(1, Math.max(0, state.scrollLeft / state.maxScroll)) : 0;
  const thumbWidthPct =
    state.scrollWidth > 0
      ? Math.max(12, Math.min(75, (state.clientWidth / state.scrollWidth) * 100))
      : 20;
  const thumbLeftPct = progress * (100 - thumbWidthPct);

  return (
    <Box
      ref={cardRef}
      sx={{
        backgroundColor: C2.white,
        borderRadius: "16px",
        border: `1px solid ${C2.border}`,
        boxShadow: "0 8px 24px rgba(26,15,59,0.06)",
        overflow: "hidden",
        p: { xs: 1, sm: 2 },
        position: "relative",
        ...sx,
      }}
    >
      {children}

      {state.show && (
        <Box
          data-c2-scroll="true"
          sx={{
            position: "fixed",
            bottom: 12,
            left: state.rect.left,
            width: state.rect.width,
            height: 42,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.2,
            zIndex: 1300,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "20px",
            border: `1.5px solid ${C2.border}`,
            boxShadow: "0 8px 30px rgba(26,15,59,0.22)",
            boxSizing: "border-box",
            userSelect: "none",
          }}
        >
          <IconButton
            size="small"
            onClick={() => handleScrollBy(-280)}
            title="Desplazar tabla a la izquierda"
            sx={{
              color: state.scrollLeft > 5 ? C2.violet : C2.muted,
              bgcolor: C2.lavender,
              p: 0.5,
              "&:hover": { bgcolor: "#DDD4F8", color: C2.bright },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box
            ref={trackRef}
            onPointerDown={handleTrackPointerDown}
            title="Haz clic o arrastra para desplazar la tabla horizontalmente"
            sx={{
              flex: 1,
              height: 14,
              borderRadius: 7,
              backgroundColor: C2.lavender,
              position: "relative",
              cursor: "pointer",
              overflow: "hidden",
              border: `1px solid ${C2.border}`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${thumbLeftPct}%`,
                width: `${thumbWidthPct}%`,
                minWidth: 32,
                borderRadius: 7,
                background: `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)`,
                boxShadow: "0 2px 6px rgba(100,17,237,0.4)",
                transition: isDraggingRef.current ? "none" : "left 0.1s ease",
              }}
            />
          </Box>

          <IconButton
            size="small"
            onClick={() => handleScrollBy(280)}
            title="Desplazar tabla a la derecha"
            sx={{
              color: state.scrollLeft < state.maxScroll - 5 ? C2.violet : C2.muted,
              bgcolor: C2.lavender,
              p: 0.5,
              "&:hover": { bgcolor: "#DDD4F8", color: C2.bright },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box
            component="span"
            sx={{
              display: { xs: "none", sm: "inline-block" },
              fontFamily: C2_FONT,
              fontSize: "0.72rem",
              fontWeight: 700,
              color: C2.muted,
              whiteSpace: "nowrap",
              pr: 0.5,
            }}
          >
            ↔ Desplazar
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function C2DocsBadge({ children, isPending, isReup }) {
  if (!isPending) return children;
  return (
    <Badge
      badgeContent={isReup ? "Nuevo doc" : "Por revisar"}
      color="warning"
      sx={{
        "& .MuiBadge-badge": {
          fontFamily: C2_FONT,
          fontSize: "0.62rem",
          fontWeight: 800,
          bgcolor: isReup ? "#FF9800" : "#ED6C02",
          color: "#FFFFFF",
          boxShadow: "0 2px 8px rgba(237,108,2,0.5)",
          whiteSpace: "nowrap",
          top: -12,
          right: 12,
          pointerEvents: "none",
        },
      }}
    >
      {children}
    </Badge>
  );
}

export function C2SectionHeading({ title, subtitle, action, children }) {
  return (
    <Box sx={{ mb: 1.5, px: 0.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
        <Typography
          sx={{
            fontFamily: C2_FONT,
            fontWeight: 800,
            fontSize: "1rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: C2.sidebar,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.85rem", color: C2.muted, mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action || children ? <Box sx={{ flexShrink: 0 }}>{action || children}</Box> : null}
    </Box>
  );
}

export function C2AddButton({ label, onClick }) {
  return (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={onClick}
      sx={{
        textTransform: "none",
        fontFamily: C2_FONT,
        fontWeight: 700,
        borderRadius: "12px",
        px: 2,
        py: 1,
        flexShrink: 0,
        background: `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)`,
        boxShadow: "0 6px 16px rgba(100,17,237,0.28)",
        "&:hover": {
          background: "linear-gradient(90deg, #5B0FD9 0%, #6D1FF0 100%)",
        },
      }}
    >
      {label}
    </Button>
  );
}

export function C2Select({ id, label, value, onChange, options = [], minWidth = 180, sx }) {
  const normOptions = (options || []).map((o) => {
    if (typeof o === "string") return { value: o, label: o };
    if (o && typeof o === "object") {
      return { value: o.value ?? "", label: o.label ?? String(o.value ?? "") };
    }
    return { value: String(o ?? ""), label: String(o ?? "") };
  });

  const selectId = id || `c2-select-${String(label || 'filter').toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <FormControl
      size="small"
      sx={{
        minWidth,
        flexShrink: 0,
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          fontFamily: C2_FONT,
          color: C2.dark,
          "& fieldset": { borderColor: C2.border },
          "&:hover fieldset": { borderColor: C2.muted },
          "&.Mui-focused fieldset": { borderColor: C2.violet },
        },
        "& .MuiInputLabel-root": { fontFamily: C2_FONT, color: C2.muted },
        "& .MuiInputLabel-root.Mui-focused": { color: C2.violet },
        ...sx,
      }}
    >
      {label ? <InputLabel id={`${selectId}-label`}>{label}</InputLabel> : null}
      <Select
        labelId={label ? `${selectId}-label` : undefined}
        id={selectId}
        label={label}
        value={value ?? ""}
        onChange={onChange}
      >
        {normOptions.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ fontFamily: C2_FONT }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export const c2Toolbar = (...extras) => ({
  Toolbar: (toolbarProps) => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        width: "100%",
        px: 0.5,
        "& .MuiToolbar-root": {
          minHeight: 56,
          paddingLeft: "0 !important",
          paddingRight: "0 !important",
          flex: "1 1 auto",
        },
      }}
    >
      <MTableToolbar {...toolbarProps} />
      {extras.filter(Boolean).map((node, i) => (
        <React.Fragment key={i}>{node}</React.Fragment>
      ))}
    </Box>
  ),
});

export function C2EmptyState({ icon: Icon = PeopleAltOutlinedIcon, title = "No hay registros para mostrar", message }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6 },
        px: 2,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 88,
          height: 88,
          borderRadius: "50%",
          backgroundColor: C2.lavender,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <Icon sx={{ fontSize: 44, color: C2.violet }} />
        <Box
          sx={{
            position: "absolute",
            right: 2,
            bottom: 2,
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${C2.violet}, ${C2.bright})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(100,17,237,0.35)",
          }}
        >
          <AddIcon sx={{ fontSize: 18, color: C2.white }} />
        </Box>
      </Box>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: "1.05rem", color: C2.sidebar, textAlign: "center" }}>
        {title}
      </Typography>
      {message ? (
        <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.9rem", color: C2.muted, textAlign: "center", maxWidth: 400, lineHeight: 1.45 }}>
          {message}
        </Typography>
      ) : null}
    </Box>
  );
}

export const c2Localization = (base, emptyState) => ({
  ...base,
  body: {
    ...(base.body || {}),
    emptyDataSourceMessage: emptyState,
  },
});

export function C2UserAvatar({ src, firstName, lastName }) {
  const initials =
    [firstName, lastName]
      .filter(Boolean)
      .map((p) => String(p).trim()[0]?.toUpperCase())
      .join("") || "?";
  return (
    <Avatar
      src={src || undefined}
      alt={initials}
      sx={{
        width: 32,
        height: 32,
        mx: "auto",
        bgcolor: C2.lavender,
        color: C2.violet,
        border: `1px solid ${C2.border}`,
        fontFamily: C2_FONT,
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      {initials}
    </Avatar>
  );
}

export function C2DocsChip({ label, icon: Icon }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 1,
        py: 0.3,
        borderRadius: "8px",
        backgroundColor: C2.lavender,
        border: `1px solid ${C2.border}`,
        color: C2.violet,
        fontFamily: C2_FONT,
        fontWeight: 700,
        fontSize: "0.76rem",
        whiteSpace: "nowrap",
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 14 }} /> : null}
      {label}
    </Box>
  );
}

export const c2PillTabsSx = {
  minHeight: 0,
  p: 0.75,
  backgroundColor: C2.white,
  border: `1px solid ${C2.border}`,
  borderRadius: "16px",
  boxShadow: "0 6px 18px rgba(26,15,59,0.05)",
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-flexContainer": { gap: 0.75 },
  "& .MuiTab-root": {
    minHeight: 42,
    px: 2,
    borderRadius: "12px",
    textTransform: "none",
    fontFamily: C2_FONT,
    fontWeight: 600,
    fontSize: "0.92rem",
    color: C2.muted,
    transition: "background-color 0.2s, color 0.2s",
    "&:hover": { backgroundColor: C2.lavender, color: C2.deep },
  },
  "& .MuiTab-root.Mui-selected": {
    color: C2.white,
    background: `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)`,
    boxShadow: "0 6px 16px rgba(100,17,237,0.28)",
  },
};

export function C2SummaryCard({ icon: Icon, label, value, caption, accent = C2.violet, children }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        borderRadius: "16px",
        backgroundColor: C2.white,
        border: `1px solid ${C2.border}`,
        boxShadow: "0 6px 18px rgba(26,15,59,0.05)",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: "12px",
          backgroundColor: C2.lavender,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ color: accent, fontSize: 24 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.8rem", color: C2.muted }}>{label}</Typography>
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: "1.4rem", color: C2.sidebar, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {caption ? (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.72rem", color: C2.muted }}>{caption}</Typography>
        ) : null}
        {children}
      </Box>
    </Box>
  );
}

export function C2ProgressBar({ used, total, done, minWidth = 120 }) {
  const safeTotal = Number(total) > 0 ? Number(total) : 0;
  const pct = safeTotal ? Math.min(100, Math.round((Number(used) / safeTotal) * 100)) : 0;
  return (
    <Box sx={{ minWidth }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: "0.82rem", color: C2.sidebar }}>
          {used} / {safeTotal}
        </Typography>
        <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.74rem", color: C2.muted }}>{pct}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: C2.lavender,
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            background: done
              ? "linear-gradient(90deg, #22C55E, #16A34A)"
              : `linear-gradient(90deg, ${C2.violet}, ${C2.bright})`,
          },
        }}
      />
    </Box>
  );
}

const CHIP_TONES = {
  violet: { backgroundColor: C2.lavender, color: C2.violet, border: `1px solid ${C2.border}` },
  success: { backgroundColor: "rgba(22,163,74,0.1)", color: "#15803D", border: "1px solid rgba(22,163,74,0.3)" },
  warning: { backgroundColor: "rgba(217,119,6,0.1)", color: "#B45309", border: "1px solid rgba(217,119,6,0.3)" },
  danger: { backgroundColor: "rgba(220,38,38,0.08)", color: "#B91C1C", border: "1px solid rgba(220,38,38,0.28)" },
  neutral: { backgroundColor: "#FFFFFF", color: C2.muted, border: `1px solid ${C2.border}` },
};

export function C2StatusChip({ tone = "violet", label, dot = true }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.25,
        py: 0.4,
        borderRadius: "999px",
        fontFamily: C2_FONT,
        fontWeight: 700,
        fontSize: "0.76rem",
        whiteSpace: "nowrap",
        ...(CHIP_TONES[tone] || CHIP_TONES.violet),
      }}
    >
      {dot ? <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }} /> : null}
      {label}
    </Box>
  );
}

export const C2Switch = (props) => (
  <Switch
    {...props}
    sx={{
      "& .MuiSwitch-switchBase.Mui-checked": { color: C2.violet },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: C2.bright,
        opacity: 0.55,
      },
      "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": { color: "#A98BF5" },
      ...props.sx,
    }}
  />
);

/**
 * Verifica si un valor es una URL o imagen válida cargada por el usuario (no un string vacío o texto cualquiera).
 */
export const isValidImageUrl = (val) => {
  if (!val || typeof val !== "string") return false;
  const s = val.trim();
  if (s.length < 10) return false;
  if (s === "null" || s === "undefined" || s === "—" || s === "-") return false;
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:image/") ||
    s.startsWith("blob:")
  );
};

/**
 * Detecta si el usuario cargó una imagen o archivo de documento real:
 * - Para conductores/líderes: foto de cédula (`verifyIdImage`) o fotos de licencia (`licenseImage` / `licenseImageBack`).
 * - Para usuarios/clientes (riders): foto de documento/cédula (`verifyIdImage`).
 * NOTA: `verifyId` es únicamente el número de cédula escrito en texto, NO una foto o documento cargado.
 */
export function hasUserUploadedDoc(user) {
  if (!user) return false;

  if (user.usertype === "driver") {
    return (
      isValidImageUrl(user.verifyIdImage) ||
      isValidImageUrl(user.licenseImage) ||
      isValidImageUrl(user.licenseImageBack)
    );
  }

  // Para usuarios clientes/riders: debe tener la foto de su documento
  return isValidImageUrl(user.verifyIdImage);
}

/**
 * Determina el estado de aprobación de un conductor:
 * - 'approved': Cuenta aprobada o documento aprobado por admin.
 * - 'rejected': Documento o cuenta rechazados por admin.
 * - 'pending': En espera de revisión (tiene documentos reales cargados).
 * - 'no_docs': No ha cargado documentos (ni cédula ni licencia).
 */
export function getDriverApprovalStatus(user) {
  if (!user) return "no_docs";
  if (user.verifyIdStatus === "rejected" || user.rejected) return "rejected";
  if (user.approved === true || user.verifyIdStatus === "approved" || user.verifyIdApproved === true) {
    return "approved";
  }
  // Si no ha cargado ningún documento real (ni foto de cédula ni foto de licencia), no está pendiente de revisión
  if (!hasUserUploadedDoc(user)) {
    return "no_docs";
  }
  return "pending";
}

/**
 * Detecta si un conductor o usuario fue rechazado previamente y volvió a subir un documento real.
 */
export function isReuploadedAfterReject(user) {
  if (!user) return false;
  // Debe tener documento físico/imagen cargado
  if (!hasUserUploadedDoc(user)) return false;

  if (user.docsReuploadedAfterReject) return true;

  // Si fue revisado previamente pero volvió a estado pendiente por actualización de datos
  if (
    user.verifyIdStatus === "pending" &&
    (user.verifyIdReviewedAt || user.verifyIdRejectCode || user.verifyIdRejectReason)
  ) {
    return true;
  }

  // Si tiene fecha de documento actualizada posterior a la fecha de revisión
  if (
    user.docsUpdatedAt &&
    user.verifyIdReviewedAt &&
    user.docsUpdatedAt > user.verifyIdReviewedAt
  ) {
    return true;
  }

  return false;
}

/**
 * Detecta si el documento del usuario ya fue aprobado por el admin.
 */
export function isUserDocVerified(user) {
  if (!user) return false;
  return Boolean(user.verifyIdApproved === true || user.verifyIdStatus === "approved");
}

/**
 * Detecta si el usuario tiene documentos reales pendientes de revisión/aprobación por administración.
 */
export function hasPendingDocReview(user) {
  if (!user) return false;
  // Solo si cargó una imagen o documento real
  if (!hasUserUploadedDoc(user)) return false;

  // Si ya está verificado y aprobado, no está pendiente
  if (isUserDocVerified(user)) return false;

  // Si fue rechazado previamente y volvió a subir documento
  if (isReuploadedAfterReject(user)) return true;

  // Si no está rechazado y tiene documento subido sin verificar, está pendiente
  if (user.verifyIdStatus !== "rejected") {
    return true;
  }

  return false;
}

/**
 * Retorna la fecha más reciente de carga o actualización de documentos para ordenar.
 */
export function getUserDocTimestamp(user) {
  if (!user) return 0;
  // Quien no ha subido documento real no tiene prioridad en la ordenación por documentos
  if (!hasUserUploadedDoc(user)) return 0;
  return Number(user.docsUpdatedAt || user.verifyIdReviewedAt || user.createdAt || 0);
}

/**
 * Badge de verificación para mostrar junto al nombre del usuario/conductor/líder.
 */
export function C2VerifiedBadge({ user, showPending = true }) {
  if (!user) return null;
  const isVerified = isUserDocVerified(user);
  const isPending = hasPendingDocReview(user);
  const isRejected = user.verifyIdStatus === "rejected";

  if (isVerified) {
    return (
      <Tooltip title="Identidad verificada: Documentos revisados y aprobados">
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.3,
            px: 0.7,
            py: 0.15,
            borderRadius: "6px",
            bgcolor: "#DCFCE7",
            color: "#15803D",
            border: "1px solid #BBF7D0",
            fontSize: "0.68rem",
            fontWeight: 800,
            fontFamily: C2_FONT,
            whiteSpace: "nowrap",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 13, color: "#16A34A" }} />
          <span>Verificado</span>
        </Box>
      </Tooltip>
    );
  }

  if (isPending && showPending) {
    const isReup = isReuploadedAfterReject(user);
    return (
      <Tooltip title={isReup ? "Documento reenviado tras rechazo: Pendiente de aprobación" : "Documento subido: Pendiente de revisión y aprobación"}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.3,
            px: 0.7,
            py: 0.15,
            borderRadius: "6px",
            bgcolor: "#FFF3E0",
            color: "#E65100",
            border: "1px solid #FFE0B2",
            fontSize: "0.68rem",
            fontWeight: 800,
            fontFamily: C2_FONT,
            whiteSpace: "nowrap",
          }}
        >
          🔔 {isReup ? "Reenviado" : "Por revisar"}
        </Box>
      </Tooltip>
    );
  }

  if (isRejected) {
    return (
      <Tooltip title="Documento rechazado por administración">
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 0.7,
            py: 0.15,
            borderRadius: "6px",
            bgcolor: "#FEE2E2",
            color: "#B91C1C",
            border: "1px solid #FECACA",
            fontSize: "0.68rem",
            fontWeight: 800,
            fontFamily: C2_FONT,
            whiteSpace: "nowrap",
          }}
        >
          ✕ Rechazado
        </Box>
      </Tooltip>
    );
  }

  return null;
}

